<script setup lang="ts">
import Bugsnag from '@bugsnag/js';
import { useQuery } from '@vue/apollo-composable';
import { computed, ref, watch, watchEffect } from 'vue';

import ButtonAddToCart from '@/components/CartActions/ButtonAddToCart.vue';
import IconChevron from '@/shared/components/UI/Icons/IconChevron.vue';
import StockIndicator from '@/shared/components/UI/StockIndicator.vue';
import {
  PatternProductsQueryResult,
  PatternProductsQueryVariables,
  QueriedProduct,
  Variant
} from '@/shared/composables/patternProducts/patternProducts.types';
import {
  patternProductQuery,
  patternSizesQuery
} from '@/shared/composables/patternProducts/patternProductsStorefront.queries';
import { useTranslations } from '@/shared/composables/useTranslations';
import { collectionUrls, productTypes } from '@/shared/constants/shopify';
import { useCartStore } from '@/shared/pinia/modules/cart';
import { useProductStore } from '@/shared/pinia/modules/product';
import { getCountryCode } from '@/shared/utils/country';
import { notifyBugsnagWithContext } from '@/shared/utils/error';
import {
  shopifyGidToId,
  shopifyIdToGid
} from '@/shared/utils/shopify/id-formatter';
import { formatPriceStorefront } from '@/shared/utils/shopify/money-formatter';

type PatternEntry = { product_id: string; variant_id: string | null };
type NeedleHookItem = { product: QueriedProduct; variant: Variant };

const { t } = useTranslations();
const productStore = useProductStore();
const cartStore = useCartStore();

const patternGid = computed(() => {
  const id = productStore.relatedPattern?.id;
  return id != null ? shopifyIdToGid(String(id), 'Product') : null;
});

const { result: sizesResult, error: sizesError } = useQuery(
  patternSizesQuery,
  () => ({ patternId: patternGid.value! }),
  () => ({ enabled: !!patternGid.value, clientId: 'storefront' })
);

const patternEntries = computed<PatternEntry[]>(() => {
  const variants = sizesResult.value?.product?.variants?.nodes ?? [];
  const seen = new Map<string, PatternEntry>();
  for (const variant of variants) {
    const raw = variant.metafield?.value;
    if (!raw) continue;
    const items: { product_id: string | null; variant_id: string | null }[] =
      JSON.parse(raw);
    items.forEach((item) => {
      if (!item.product_id) return;
      const key = `${item.product_id}:${item.variant_id ?? ''}`;
      if (!seen.has(key)) {
        seen.set(key, {
          product_id: item.product_id,
          variant_id: item.variant_id
        });
      }
    });
  }
  return Array.from(seen.values());
});

const allProductIds = computed(() =>
  patternEntries.value.map((e) => e.product_id)
);

const {
  result: productsResult,
  loading,
  error: productsError
} = useQuery<PatternProductsQueryResult, PatternProductsQueryVariables>(
  patternProductQuery,
  () => ({ ids: allProductIds.value, country: getCountryCode() }),
  () => ({ enabled: allProductIds.value.length > 0, clientId: 'storefront' })
);

const hasError = ref(false);

watchEffect(() => {
  const error = sizesError.value ?? productsError.value;
  hasError.value = !!error;
  if (error && Bugsnag.isStarted()) {
    notifyBugsnagWithContext('KitNeedleHookUpsell', error);
  }
});

const needlesAndHooks = computed<NeedleHookItem[]>(() => {
  const nodes = productsResult.value?.nodes ?? [];
  const productMap = new Map<string, QueriedProduct>();
  for (const node of nodes) {
    if (
      node &&
      (node.productType === productTypes.NEEDLES ||
        node.productType === productTypes.HOOKS)
    ) {
      productMap.set(node.id, node);
    }
  }
  return patternEntries.value.flatMap((entry) => {
    const product = productMap.get(entry.product_id);
    if (!product) return [];
    const variant =
      product.variants.nodes.find((v) => v.id === entry.variant_id) ??
      product.variants.nodes[0];
    if (!variant) return [];
    return [{ product, variant }];
  });
});

const checkedVariantIds = ref<Set<string>>(new Set());

const isOutOfStock = (variant: Variant) => variant.quantityAvailable <= 0;

const oosItems = computed(() =>
  needlesAndHooks.value.filter((i) => isOutOfStock(i.variant))
);

const dominantType = computed(() => {
  const needlesCount = needlesAndHooks.value.filter(
    (i) => i.product.productType === productTypes.NEEDLES
  ).length;
  const hooksCount = needlesAndHooks.value.filter(
    (i) => i.product.productType === productTypes.HOOKS
  ).length;
  return needlesCount >= hooksCount ? productTypes.NEEDLES : productTypes.HOOKS;
});

const heading = computed(() =>
  dominantType.value === productTypes.NEEDLES
    ? t('product.kits.upsell.heading_needles')
    : t('product.kits.upsell.heading_hook')
);

const checkedCount = computed(() => checkedVariantIds.value.size);

const isMixedTypes = computed(() => {
  const inStock = needlesAndHooks.value.filter((i) => !isOutOfStock(i.variant));
  const relevantItems = inStock.length > 0 ? inStock : needlesAndHooks.value;
  const types = new Set(relevantItems.map((i) => i.product.productType));
  return types.has(productTypes.NEEDLES) && types.has(productTypes.HOOKS);
});

const ctaLabel = computed(() => {
  if (isMixedTypes.value)
    return t('product.kits.upsell.cta_all', checkedCount.value);
  const inStock = needlesAndHooks.value.filter((i) => !isOutOfStock(i.variant));
  const relevantItems = inStock.length > 0 ? inStock : needlesAndHooks.value;
  const types = new Set(relevantItems.map((i) => i.product.productType));
  return types.has(productTypes.NEEDLES)
    ? t('product.kits.upsell.cta_needles', checkedCount.value)
    : t('product.kits.upsell.cta_hook', checkedCount.value);
});

const oosTypes = computed(
  () => new Set(oosItems.value.map((i) => i.product.productType))
);

const browseLabel = computed(() => {
  const hasNeedles = oosTypes.value.has(productTypes.NEEDLES);
  const hasHooks = oosTypes.value.has(productTypes.HOOKS);
  if (hasNeedles && hasHooks) return t('product.kits.upsell.browse_all');
  if (hasNeedles) return t('product.kits.upsell.browse_needles');
  return t('product.kits.upsell.browse_hook');
});

const browseUrl = computed(() => {
  const isMixed = oosTypes.value.size > 1;
  const base = isMixed
    ? collectionUrls.ALL
    : oosTypes.value.has(productTypes.NEEDLES)
      ? collectionUrls.KNITTING_NEEDLES
      : collectionUrls.CROCHET_HOOKS;

  // hobbii.sizes is a list metafield serialized as a JSON array string e.g. '["3.5mm","4mm"]'.
  // Search & Adapt is configured to filter by this key, regardless of locale.
  const unique = [
    ...new Set(
      oosItems.value.flatMap((i) => {
        try {
          return JSON.parse(i.variant.sizes?.value ?? '[]') as string[];
        } catch {
          return [];
        }
      })
    )
  ];
  if (unique.length === 0) return base;

  const params = unique
    .map((v) => `filter.v.m.hobbii.sizes=${encodeURIComponent(v)}`)
    .join('&');
  return `${base}?${params}`;
});

const initChecked = () => {
  checkedVariantIds.value = new Set(
    needlesAndHooks.value
      .filter((i) => !isOutOfStock(i.variant))
      .map((i) => i.variant.id)
  );
};

watch(needlesAndHooks, initChecked, { immediate: true });

const toggleItem = (item: NeedleHookItem) => {
  if (isOutOfStock(item.variant)) return;
  const next = new Set(checkedVariantIds.value);
  if (next.has(item.variant.id)) {
    next.delete(item.variant.id);
  } else {
    next.add(item.variant.id);
  }
  checkedVariantIds.value = next;
};

const addToCart = async () => {
  const items = needlesAndHooks.value
    .filter((i) => checkedVariantIds.value.has(i.variant.id))
    .map((i) => ({ id: Number(shopifyGidToId(i.variant.id)), quantity: 1 }));
  if (items.length === 0) return;
  await cartStore.addIndependentProductsToCart(items);
};
</script>

<template>
  <div
    v-if="!hasError && (needlesAndHooks.length > 0 || loading)"
    class="flex flex-col gap-2"
  >
    <div class="rounded-xl bg-gray-50 p-6">
      <template v-if="loading">
        <div class="space-y-3">
          <div
            v-for="cls in ['h-5 w-32 rounded', 'h-4 w-48 rounded']"
            :key="cls"
            :class="`${cls} relative animate-pulse overflow-hidden bg-gray-lightest`"
          >
            <div
              class="absolute inset-0 -translate-x-full bg-gradient-to-r from-gray-lightest via-white to-gray-lightest"
            />
          </div>
          <div class="flex gap-3 pt-1">
            <div
              class="relative size-16 animate-pulse overflow-hidden rounded-lg bg-gray-lightest"
            >
              <div
                class="absolute inset-0 -translate-x-full bg-gradient-to-r from-gray-lightest via-white to-gray-lightest"
              />
            </div>
            <div class="flex-1 space-y-2">
              <div
                v-for="cls in [
                  'h-4 w-3/4 rounded',
                  'h-3 w-1/3 rounded',
                  'h-3 w-1/4 rounded'
                ]"
                :key="cls"
                :class="`${cls} relative animate-pulse overflow-hidden bg-gray-lightest`"
              >
                <div
                  class="absolute inset-0 -translate-x-full bg-gradient-to-r from-gray-lightest via-white to-gray-lightest"
                />
              </div>
            </div>
          </div>
          <div
            class="relative mt-4 h-12 w-full animate-pulse overflow-hidden rounded-full bg-gray-lightest"
          >
            <div
              class="absolute inset-0 -translate-x-full bg-gradient-to-r from-gray-lightest via-white to-gray-lightest"
            />
          </div>
        </div>
      </template>

      <template v-else-if="needlesAndHooks.length > 0">
        <h2 class="text-lg font-bold">{{ heading }}</h2>
        <p class="mt-1 text-base text-gray-700">
          {{ t('product.kits.upsell.subheading') }}
        </p>

        <div class="mt-4 space-y-6">
          <div
            v-for="item in needlesAndHooks"
            :key="item.variant.id"
            class="flex items-center gap-4 pb-1"
          >
            <button
              v-if="needlesAndHooks.length > 1"
              class="shrink-0"
              :aria-checked="checkedVariantIds.has(item.variant.id)"
              :aria-disabled="isOutOfStock(item.variant)"
              role="checkbox"
              :class="{ 'cursor-not-allowed': isOutOfStock(item.variant) }"
              @click="toggleItem(item)"
            >
              <span
                class="flex size-6 items-center justify-center rounded"
                :class="
                  checkedVariantIds.has(item.variant.id)
                    ? 'bg-blue-checkmark'
                    : 'border border-gray-300 bg-white'
                "
              >
                <svg
                  v-if="checkedVariantIds.has(item.variant.id)"
                  class="size-4 text-white"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M3 8l3.5 3.5L13 5"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
            </button>

            <div
              class="relative size-20 shrink-0 overflow-hidden rounded-lg border border-gray-300"
              :class="{ 'opacity-60': isOutOfStock(item.variant) }"
            >
              <img
                v-if="
                  item.variant.image?.url || item.product.featuredImage?.url
                "
                :src="
                  item.variant.image?.url || item.product.featuredImage?.url
                "
                :alt="item.product.featuredImage?.altText || ''"
                class="size-full object-contain"
              />
              <template v-if="isOutOfStock(item.variant)">
                <span
                  class="absolute left-0 top-1/2 z-10 block h-1 w-full origin-center rotate-45 bg-white"
                />
                <span
                  class="absolute left-0 top-1/2 z-10 block h-1 w-full origin-center -rotate-45 bg-white"
                />
              </template>
            </div>

            <div class="min-w-0 flex-1">
              <a
                :href="`/products/${item.product.handle}?variant=${shopifyGidToId(item.variant.id)}`"
                target="_blank"
                rel="noopener noreferrer"
                class="text-lg font-bold leading-snug hover:underline"
              >
                {{ item.product.title }}
              </a>
              <p class="text-base text-gray-700">{{ item.variant.title }}</p>
              <StockIndicator :in-stock="item.variant.quantityAvailable" />
              <p
                v-if="!isOutOfStock(item.variant)"
                class="text-base text-gray-700"
              >
                {{ formatPriceStorefront(item.variant.price) }}
              </p>
            </div>
          </div>
        </div>

        <div class="mt-4">
          <ButtonAddToCart
            :label="ctaLabel"
            :on-action="addToCart"
            :negative="true"
            :disabled="checkedVariantIds.size === 0"
          />
        </div>
      </template>
    </div>

    <a
      v-if="!loading && oosItems.length > 0"
      :href="browseUrl"
      target="_blank"
      rel="noopener noreferrer"
      class="flex items-center justify-end gap-1 py-2 font-bold text-blue-checkmark hover:underline"
    >
      {{ browseLabel }}
      <IconChevron direction="right" class="text-lg" />
    </a>
  </div>
</template>
